import type { StandardObservationKey } from "@repo/domain";
import {
  isStandardParameter,
  STANDARD_PARAMETER_KEYS,
  STANDARD_PARAMETERS,
  standardParameterNamed,
} from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { HeartPulse, Layers, type LucideIcon, Repeat, Timer } from "lucide-react";
import type { ClinicalDefinitionFormValues } from "@/features/catalog/forms/clinical-definition-form";
import { withFieldGroup } from "@/shared/forms/app-form";

function TogglePill({
  label,
  icon: Icon,
  enabled,
  onChange,
  onBlur,
  disabled,
  title,
}: {
  label: string;
  icon: LucideIcon;
  enabled: boolean;
  onChange: () => void;
  onBlur: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={label === "Tolerance" ? "Chart tolerance" : `${label} ${enabled ? "on" : "off"}`}
      aria-pressed={enabled}
      onClick={onChange}
      onBlur={onBlur}
      disabled={disabled}
      title={title}
      className={cn(
        "h-7 w-fit gap-1.5 rounded-full px-2 text-xs font-normal hover:text-foreground",
        enabled ? "bg-muted text-foreground" : "text-muted-foreground",
      )}
    >
      <Icon className="size-3.5" />
      {label} {enabled ? "on" : "off"}
    </Button>
  );
}

const PARAMETER_ICONS = { reps: Repeat, sets: Layers, duration: Timer };
const PARAMETERS = STANDARD_PARAMETER_KEYS.map((key) => ({
  key,
  label: STANDARD_PARAMETERS[key].label,
  icon: PARAMETER_ICONS[key],
}));
export const StandardsProperty = withFieldGroup({
  defaultValues: {
    standardObservations: ["tolerance"] as StandardObservationKey[],
    parameters: [] as ClinicalDefinitionFormValues["parameters"],
  },
  props: {} as { disabled?: boolean },
  render: function Render({ group, disabled }) {
    return (
      <>
        <group.Field name="standardObservations">
          {(field) => (
            <TogglePill
              label="Tolerance"
              icon={HeartPulse}
              enabled={field.state.value.includes("tolerance")}
              onChange={() => field.handleChange(field.state.value.includes("tolerance") ? [] : ["tolerance"])}
              onBlur={field.handleBlur}
              disabled={disabled}
            />
          )}
        </group.Field>
        <group.Field name="parameters">
          {(field) => (
            <>
              {PARAMETERS.map(({ key, label, icon }) => {
                const enabled = field.state.value.some((item) => isStandardParameter(item) && item.key === key);
                const taken =
                  !enabled &&
                  field.state.value.some(
                    (item) => !isStandardParameter(item) && standardParameterNamed(item.label) === key,
                  );
                return (
                  <TogglePill
                    key={key}
                    label={label}
                    icon={icon}
                    enabled={enabled}
                    onChange={() =>
                      field.handleChange(
                        enabled
                          ? field.state.value.filter((item) => !isStandardParameter(item) || item.key !== key)
                          : [...field.state.value, { kind: "standard", key }],
                      )
                    }
                    onBlur={field.handleBlur}
                    disabled={disabled || taken}
                    title={taken ? `A parameter is already named ${label}. Rename it to turn ${label} on.` : undefined}
                  />
                );
              })}
            </>
          )}
        </group.Field>
      </>
    );
  },
});
